/*────────────────────── Enhanced Onboarding Error Handling ──────────────────────*/

import { getApiUrl } from './apiClient.js';

// Error types for different onboarding scenarios
export const ERROR_TYPES = {
    NETWORK: 'network',
    AUTHENTICATION: 'authentication',
    VALIDATION: 'validation',
    BILLING: 'billing',
    PHONE: 'phone',
    PARROT: 'parrot',
    SESSION: 'session',
    STRIPE: 'stripe',
    TWILIO: 'twilio',
    OPENAI: 'openai',
    TIMEOUT: 'timeout',
    RATE_LIMIT: 'rate_limit',
    UNKNOWN: 'unknown'
  };
  
  // Error severity levels
  export const ERROR_SEVERITY = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical'
  };
  
  // Retry strategies
  export const RETRY_STRATEGIES = {
    IMMEDIATE: 'immediate',
    EXPONENTIAL_BACKOFF: 'exponential_backoff',
    LINEAR_BACKOFF: 'linear_backoff',
    NO_RETRY: 'no_retry'
  };
  
  /**
   * Enhanced error class for onboarding-specific errors
   */
  export class OnboardingError extends Error {
    constructor(message, type = ERROR_TYPES.UNKNOWN, severity = ERROR_SEVERITY.MEDIUM, options = {}) {
      super(message);
      this.name = 'OnboardingError';
      this.type = type;
      this.severity = severity;
      this.step = options.step || null;
      this.retryable = options.retryable !== false;
      this.retryStrategy = options.retryStrategy || RETRY_STRATEGIES.EXPONENTIAL_BACKOFF;
      this.maxRetries = options.maxRetries || 3;
      this.userMessage = options.userMessage || this.getDefaultUserMessage();
      this.recoveryActions = options.recoveryActions || this.getDefaultRecoveryActions();
      this.timestamp = new Date().toISOString();
      this.context = options.context || {};
    }
  
    getDefaultUserMessage() {
      switch (this.type) {
        case ERROR_TYPES.NETWORK:
          return "We're having trouble connecting to our servers. Please check your internet connection and try again.";
        case ERROR_TYPES.AUTHENTICATION:
          return "Your session has expired. Please sign in again to continue.";
        case ERROR_TYPES.VALIDATION:
          return "Please check your information and try again.";
        case ERROR_TYPES.BILLING:
          return "There was an issue processing your payment. Please check your payment method and try again.";
        case ERROR_TYPES.PHONE:
          return "We couldn't process your phone number selection. Please try choosing a different number.";
        case ERROR_TYPES.PARROT:
          return "There was an issue creating your Parrot. Please try again.";
        case ERROR_TYPES.SESSION:
          return "Your onboarding session has expired. We'll restart the process for you.";
        default:
          return "Something went wrong. Please try again or contact support if the problem persists.";
      }
    }
  
    getDefaultRecoveryActions() {
      switch (this.type) {
        case ERROR_TYPES.NETWORK:
          return [
            { label: 'Try Again', action: 'retry' },
            { label: 'Check Connection', action: 'check_connection' }
          ];
        case ERROR_TYPES.AUTHENTICATION:
          return [
            { label: 'Sign In Again', action: 'reauthenticate' }
          ];
        case ERROR_TYPES.VALIDATION:
          return [
            { label: 'Fix Information', action: 'edit' },
            { label: 'Try Again', action: 'retry' }
          ];
        case ERROR_TYPES.BILLING:
          return [
            { label: 'Update Payment Method', action: 'update_payment' },
            { label: 'Try Again', action: 'retry' },
            { label: 'Contact Support', action: 'contact_support' }
          ];
        case ERROR_TYPES.PHONE:
          return [
            { label: 'Choose Different Number', action: 'select_different_phone' },
            { label: 'Skip Phone Number', action: 'skip_phone' },
            { label: 'Try Again', action: 'retry' }
          ];
        case ERROR_TYPES.PARROT:
          return [
            { label: 'Try Again', action: 'retry' },
            { label: 'Start Over', action: 'restart_parrot_creation' }
          ];
        case ERROR_TYPES.SESSION:
          return [
            { label: 'Restart Onboarding', action: 'restart_onboarding' }
          ];
        default:
          return [
            { label: 'Try Again', action: 'retry' },
            { label: 'Contact Support', action: 'contact_support' }
          ];
      }
    }
  }
  
  /**
   * Parse API errors and convert them to OnboardingError instances
   */
  export const parseApiError = async (response, step = null) => {
    let errorData;
    
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: response.statusText || 'Unknown error' };
    }
  
    const status = response.status;
    const message = errorData.error || errorData.message || 'An error occurred';
  
    // Determine error type based on status code and message
    let type = ERROR_TYPES.UNKNOWN;
    let severity = ERROR_SEVERITY.MEDIUM;
    let retryable = true;
    let retryStrategy = RETRY_STRATEGIES.EXPONENTIAL_BACKOFF;
    let maxRetries = 3;
  
    // Status code based classification
    if (status === 401 || status === 403) {
      type = ERROR_TYPES.AUTHENTICATION;
      severity = ERROR_SEVERITY.HIGH;
      retryable = false;
    } else if (status === 400) {
      type = ERROR_TYPES.VALIDATION;
      severity = ERROR_SEVERITY.LOW;
      retryable = false;
    } else if (status === 408 || status === 504) {
      type = ERROR_TYPES.TIMEOUT;
      severity = ERROR_SEVERITY.MEDIUM;
      retryStrategy = RETRY_STRATEGIES.LINEAR_BACKOFF;
      maxRetries = 2;
    } else if (status === 429) {
      type = ERROR_TYPES.RATE_LIMIT;
      severity = ERROR_SEVERITY.MEDIUM;
      retryStrategy = RETRY_STRATEGIES.EXPONENTIAL_BACKOFF;
      maxRetries = 5;
    } else if (status >= 500) {
      type = ERROR_TYPES.NETWORK;
      severity = ERROR_SEVERITY.HIGH;
      retryStrategy = RETRY_STRATEGIES.LINEAR_BACKOFF;
    }
  
    // Message-based classification for external service errors (highest priority)
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('stripe')) {
      type = ERROR_TYPES.STRIPE;
      severity = ERROR_SEVERITY.HIGH;
      retryable = lowerMessage.includes('temporary') || lowerMessage.includes('try again');
    } else if (lowerMessage.includes('twilio')) {
      type = ERROR_TYPES.TWILIO;
      severity = ERROR_SEVERITY.MEDIUM;
    } else if (lowerMessage.includes('openai') || (lowerMessage.includes('chat') && !lowerMessage.includes('phone')) || (lowerMessage.includes(' ai ') || lowerMessage.startsWith('ai ') || lowerMessage.endsWith(' ai'))) {
      type = ERROR_TYPES.OPENAI;
      severity = ERROR_SEVERITY.MEDIUM;
    } else {
      // Step-specific error type detection (only if no external service detected)
      if (step) {
        if (step === 1) {
          if (lowerMessage.includes('billing') || lowerMessage.includes('plan') || lowerMessage.includes('payment')) {
            type = ERROR_TYPES.BILLING;
          }
        } else if (step === 2) {
          if (lowerMessage.includes('phone') || lowerMessage.includes('number')) {
            type = ERROR_TYPES.PHONE;
          }
        } else if (step === 3) {
          if (lowerMessage.includes('parrot') || lowerMessage.includes('agent')) {
            type = ERROR_TYPES.PARROT;
          }
        }
      }
    }
  
    return new OnboardingError(message, type, severity, {
      step,
      retryable,
      retryStrategy,
      maxRetries,
      context: {
        status,
        response: errorData,
        url: response.url,
        timestamp: new Date().toISOString()
      }
    });
  };
  
  /**
   * Retry mechanism with different strategies
   */
  export class RetryManager {
    constructor() {
      this.retryAttempts = new Map();
    }
  
    async executeWithRetry(operation, errorHandler, maxRetries = 3, strategy = RETRY_STRATEGIES.EXPONENTIAL_BACKOFF) {
      const operationId = this.generateOperationId(operation);
      let attempts = 0;
  
      while (attempts <= maxRetries) {
        try {
          const result = await operation();
          this.retryAttempts.delete(operationId);
          return result;
        } catch (error) {
          attempts++;
          this.retryAttempts.set(operationId, attempts);
  
          const onboardingError = error instanceof OnboardingError 
            ? error 
            : new OnboardingError(error.message, ERROR_TYPES.UNKNOWN, ERROR_SEVERITY.MEDIUM);
  
          // Don't retry if error is not retryable or we've exceeded max retries
          if (!onboardingError.retryable || attempts > maxRetries) {
            this.retryAttempts.delete(operationId);
            throw onboardingError;
          }
  
          // Calculate delay based on strategy
          const delay = this.calculateDelay(strategy, attempts);
          
          // Notify error handler about retry attempt
          if (errorHandler) {
            errorHandler(onboardingError, attempts, maxRetries, delay);
          }
  
          // Wait before retry
          await this.sleep(delay);
        }
      }
    }
  
    calculateDelay(strategy, attempt) {
      switch (strategy) {
        case RETRY_STRATEGIES.IMMEDIATE:
          return 0;
        case RETRY_STRATEGIES.LINEAR_BACKOFF:
          return attempt * 1000; // 1s, 2s, 3s...
        case RETRY_STRATEGIES.EXPONENTIAL_BACKOFF:
          return Math.min(1000 * Math.pow(2, attempt - 1), 10000); // 1s, 2s, 4s, 8s, max 10s
        default:
          return 1000;
      }
    }
  
    generateOperationId(operation) {
      return operation.name || operation.toString().slice(0, 50);
    }
  
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  
    reset() {
      this.retryAttempts.clear();
    }
  }
  
  /**
   * Error logging and monitoring
   */
  export const logOnboardingError = (error, context = {}) => {
    const errorLog = {
      timestamp: new Date().toISOString(),
      type: error.type || ERROR_TYPES.UNKNOWN,
      severity: error.severity || ERROR_SEVERITY.MEDIUM,
      message: error.message,
      step: error.step,
      context: {
        ...error.context,
        ...context,
        userAgent: navigator.userAgent,
        url: window.location.href
      }
    };
  
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Onboarding Error:', errorLog);
    }
  
    // Send to monitoring service (implement based on your monitoring setup)
    try {
      // Example: send to analytics or error tracking service
      // analytics.track('onboarding_error', errorLog);
    } catch (loggingError) {
      console.warn('Failed to log onboarding error:', loggingError);
    }
  
    return errorLog;
  };
  
  /**
   * Recovery action handlers
   */
  export const recoveryActionHandlers = {
    retry: (operation) => operation(),
    
    reauthenticate: () => {
      window.location.href = '/sign-in?redirect=' + encodeURIComponent(window.location.pathname);
    },
    
    restart_onboarding: () => {
      window.location.href = '/onboarding';
    },
    
    contact_support: () => {
      window.open('mailto:support@parrotmate.com?subject=Onboarding Issue', '_blank');
    },
    
    check_connection: async () => {
      // Simple connectivity check using API config
      return fetch(getApiUrl('/api/health'), { method: 'HEAD' })
        .then(() => true)
        .catch(() => false);
    },
    
    update_payment: () => {
      window.location.href = '/billing';
    },
    
    skip_phone: (onNext) => {
      if (onNext) onNext();
    },
    
    select_different_phone: (onReset) => {
      if (onReset) onReset();
    },
    
    restart_parrot_creation: (onRestart) => {
      if (onRestart) onRestart();
    }
  };
  
  /**
   * Validation helpers for onboarding steps
   */
  export const validateStep = (step, data) => {
    const errors = [];
  
    switch (step) {
      case 1: // Plan selection
        if (!data.selectedPlan) {
          errors.push(new OnboardingError(
            'Please select a plan to continue',
            ERROR_TYPES.VALIDATION,
            ERROR_SEVERITY.LOW,
            { step: 1, retryable: false }
          ));
        }
        break;
  
      case 2: // Phone number (optional)
        // Phone number is optional, so no validation errors
        break;
  
      case 3: // Parrot creation
        if (!data.parrotConfig || !data.parrotConfig.name) {
          errors.push(new OnboardingError(
            'Please provide a name for your Parrot',
            ERROR_TYPES.VALIDATION,
            ERROR_SEVERITY.LOW,
            { step: 3, retryable: false }
          ));
        }
        break;
    }
  
    return errors;
  };
  
  /**
   * Create a centralized error handler for onboarding components
   */
  export const createOnboardingErrorHandler = (setError, setLoading) => {
    const retryManager = new RetryManager();
  
    return {
      handleError: (error, context = {}) => {
        const onboardingError = error instanceof OnboardingError 
          ? error 
          : new OnboardingError(error.message, ERROR_TYPES.UNKNOWN, ERROR_SEVERITY.MEDIUM);
  
        // Log the error
        logOnboardingError(onboardingError, context);
  
        // Update UI state
        if (setLoading) setLoading(false);
        if (setError) setError(onboardingError);
  
        return onboardingError;
      },
  
      executeWithRetry: (operation, options = {}) => {
        const { maxRetries = 3, strategy = RETRY_STRATEGIES.EXPONENTIAL_BACKOFF } = options;
        
        return retryManager.executeWithRetry(
          operation,
          (error, attempt, maxAttempts, delay) => {
            // Retry attempt
          },
          maxRetries,
          strategy
        );
      },
  
      reset: () => {
        retryManager.reset();
        if (setError) setError(null);
      }
    };
  };

  /**
   * Enhanced error boundary for onboarding steps
   * Note: This should be implemented as a React component in a separate .jsx file
   */
  export const createOnboardingErrorBoundary = (step) => {
    return {
      step,
      // This would be implemented as a React component wrapper
      // See OnboardingErrorDisplay.jsx for the actual implementation
    };
  };

  /**
   * Step-specific error messages and recovery options
   */
  export const getStepSpecificErrorInfo = (error, step) => {
    const baseInfo = {
      title: 'Something went wrong',
      description: error.userMessage || error.message,
      actions: error.recoveryActions || []
    };

    switch (step) {
      case 1: // Plan Selection
        return {
          ...baseInfo,
          title: 'Plan Selection Error',
          description: error.type === ERROR_TYPES.STRIPE 
            ? 'There was an issue processing your payment. Please check your payment method and try again.'
            : error.type === ERROR_TYPES.BILLING
            ? 'Unable to set up your billing plan. Please try again or contact support.'
            : baseInfo.description,
          actions: [
            ...(error.retryable ? [{ label: 'Try Again', action: 'retry', primary: true }] : []),
            { label: 'Update Payment Method', action: 'update_payment' },
            { label: 'Contact Support', action: 'contact_support' }
          ]
        };

      case 2: // Phone Number Search
        return {
          ...baseInfo,
          title: 'Phone Number Error',
          description: error.type === ERROR_TYPES.TWILIO
            ? 'Unable to search for phone numbers. Please try different search criteria.'
            : error.type === ERROR_TYPES.PHONE
            ? 'There was an issue with your phone number selection. Please try again.'
            : baseInfo.description,
          actions: [
            ...(error.retryable ? [{ label: 'Try Again', action: 'retry', primary: true }] : []),
            { label: 'Try Different Search', action: 'select_different_phone' },
            { label: 'Skip Phone Number', action: 'skip_phone' },
            { label: 'Contact Support', action: 'contact_support' }
          ]
        };

      case 3: // Parrot Creation
        return {
          ...baseInfo,
          title: 'Parrot Creation Error',
          description: error.type === ERROR_TYPES.OPENAI
            ? 'Unable to configure your Parrot\'s personality. Please try again.'
            : error.type === ERROR_TYPES.PARROT
            ? 'There was an issue creating your Parrot. Please try again.'
            : baseInfo.description,
          actions: [
            ...(error.retryable ? [{ label: 'Try Again', action: 'retry', primary: true }] : []),
            { label: 'Start Over', action: 'restart_parrot_creation' },
            { label: 'Contact Support', action: 'contact_support' }
          ]
        };

      default:
        return baseInfo;
    }
  };

  /**
   * Network connectivity checker
   */
  export const checkNetworkConnectivity = async () => {
    try {
      const response = await fetch(getApiUrl('/api/health'), { 
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  /**
   * Enhanced error monitoring and analytics
   */
  export const trackOnboardingError = (error, context = {}) => {
    const errorEvent = {
      event: 'onboarding_error',
      properties: {
        error_type: error.type,
        error_severity: error.severity,
        error_message: error.message,
        step: error.step,
        retryable: error.retryable,
        retry_strategy: error.retryStrategy,
        timestamp: error.timestamp,
        user_agent: navigator.userAgent,
        url: window.location.href,
        ...context
      }
    };

    // Send to analytics service (implement based on your setup)
    try {
      // Example: analytics.track(errorEvent.event, errorEvent.properties);
      // Onboarding Error Tracked
    } catch (trackingError) {
      console.warn('Failed to track onboarding error:', trackingError);
    }

    return errorEvent;
  };
  
